/**
 * Sorting and pagination for Cloudscape tables.
 *
 * Pure functions — no React, no Cloudscape.
 *
 * WHY THIS EXISTS
 * ---------------
 * Cloudscape's `Table` is a CONTROLLED component for sorting. Its own interface says
 * `sortingColumn` is "the definition object of the currently sorted column" and
 * `sortingDisabled` "specifies if sorting BUTTONS are disabled" — the table draws the
 * header affordance and reports clicks, but it never reorders `items`. So
 * `sortingDisabled={false}` plus `sortingField` on the columns produces a header that
 * looks clickable and does nothing at all.
 *
 * Likewise `pagination` is a SLOT (`pagination?: React.ReactNode`). Passing the bare
 * JSX attribute means `pagination={true}`, and React renders a boolean as nothing, so
 * it silently no-ops rather than failing.
 *
 * Both mistakes are invisible: nothing throws, and a mocked Table that ignores those
 * props will not catch them either. Hence pure helpers with direct unit tests.
 *
 * Mirrors the manual pattern already used by FundingRequestTable.js rather than
 * @cloudscape-design/collection-hooks' useCollection: nothing else in this repo uses
 * that hook, and collection-hooks is only a transitive dependency of the components
 * package, not a declared one.
 */

/** Rows per page. Applies to both Rubicon tables. */
export const PAGE_SIZE = 25;

/**
 * Sort a copy of `items` by a Cloudscape `sortingColumn`.
 *
 * Returns the list UNTOUCHED when no column is selected, so the payload's own order is
 * what a reviewer sees until they choose to sort.
 *
 * Nulls sort last in BOTH directions — an absent value is not "smallest", and flipping
 * direction should not parade the unpriced rows to the top.
 */
export const sortItems = (items, sortingColumn, isDescending = false) => {
  const list = items || [];
  const field = sortingColumn?.sortingField;
  if (!field) return list;

  return [...list].sort((a, b) => {
    const aVal = a?.[field];
    const bVal = b?.[field];

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    const comparison = typeof aVal === 'string' && typeof bVal === 'string'
      ? aVal.localeCompare(bVal)
      : Number(aVal) - Number(bVal);

    return isDescending ? -comparison : comparison;
  });
};

/** Total pages, never below 1 so an empty table still has a valid page index. */
export const pageCountOf = (items, pageSize = PAGE_SIZE) =>
  Math.max(1, Math.ceil(((items || []).length) / pageSize));

/**
 * The slice for `pageIndex` (1-based).
 *
 * An out-of-range index falls back to page 1 rather than rendering an empty table —
 * reachable whenever the row set shrinks under a filter while a later page is selected.
 */
export const paginateItems = (items, pageIndex = 1, pageSize = PAGE_SIZE) => {
  const list = items || [];
  const page = clampPage(list, pageIndex, pageSize);
  return list.slice((page - 1) * pageSize, page * pageSize);
};

/** The page index actually used by paginateItems, for the Pagination control to match. */
export const clampPage = (items, pageIndex = 1, pageSize = PAGE_SIZE) => {
  const list = items || [];
  const requested = Number(pageIndex) || 1;
  if (requested < 1) return 1;
  const count = pageCountOf(list, pageSize);
  return requested > count ? 1 : requested;
};
